import React, { useCallback, useEffect, useState, useRef, memo } from "react";
import GameCanvas from "./canvas";
import Snake from "../classes/snake";
import Food from "../classes/food";
import Score from "./score";

const snake = new Snake();
const food = new Food();

//store score outside of function component to ensure gameLoop isn't reinitialised everytime state score is updated
let score = 0;

const fps = 60;
const frameDuration = 1000 / fps;
let then,
  elapsedTimeBetweenFrames,
  accumulatedFrameTime = 0;

const Game = ({ inputController }) => {
  const [displayScore, setDisplayScore] = useState(0);
  const canvasRef = useRef();

  const gameLoop = useCallback(
    (context, now) => {
      //calculate time between frames to determine when to update
      elapsedTimeBetweenFrames = now - then;
      then = now;

      accumulatedFrameTime += elapsedTimeBetweenFrames;

      while (accumulatedFrameTime >= frameDuration) {
        updateGame(
          context,
          inputController.getInputState(),
          frameDuration / 1000,
          setDisplayScore
        );

        accumulatedFrameTime -= frameDuration;
      }

      drawGame(context);

      requestAnimationFrame((timestamp) => gameLoop(context, timestamp));
    },
    [inputController]
  );

  //update after render to ensure we have reference to canvas
  useEffect(() => {
    const context = canvasRef.current.getContext("2d");

    then = window.performance.now();

    food.respawn(context);

    gameLoop(context, then);
  }, [gameLoop]);

  return (
    <div>
      <Score score={displayScore} />
      <GameCanvas canvasRef={canvasRef} />
    </div>
  );
};

const updateGame = (context, inputState, deltaTime, setDisplayScore) => {
  //avoid passing inputState? events?
  snake.updateDirection(inputState);
  snake.updatePosition(deltaTime);

  const resetGame = () => {
    //can we avoid passing setDisplayScore?
    score = 0;
    setDisplayScore(0);

    snake.reset();
    food.respawn(context);
  };

  if (hasCollided(snake.getHead(), food)) {
    snake.grow();
    snake.increaseSpeed();
    food.respawn(context);

    setDisplayScore(++score);
  }

  if (hasCollidedWithBoundary(context.canvas, snake.getHead())) {
    resetGame();
  }

  if (
    snake.isFullSize() &&
    snake.getTail().some((tailSegment) => {
      return hasCollided(snake.getHead(), tailSegment);
    })
  ) {
    resetGame();
  }
};

const drawGame = (context) => {
  context.clearRect(
    0,
    0,
    context.canvas.clientWidth,
    context.canvas.clientHeight
  );

  snake.draw(context);
  food.draw(context);
};

const hasCollidedWithBoundary = ({ width, height }, { x, y }) =>
  x < 0 || x > width || y < 0 || y > height;

const hasCollided = (obj1, obj2) => {
  return (
    obj1.x < obj2.x + obj2.size &&
    obj1.x + obj1.size > obj2.x &&
    obj1.y < obj2.y + obj2.size &&
    obj1.y + obj1.size > obj2.y
  );
};

export default memo(Game);
